import PropTypes from 'prop-types';
import React from 'react';
import ButtonV2 from 'components/ButtonV2';
import { Radio } from 'components/FormFields';
import { Icon } from 'components/v3';
import FormsSelect from '../../components/FormsSelect';
import { brand } from '../../shared/brand';
import { LIMITS } from '../../shared/fieldTypes';
import { useBuilder } from '../BuilderContext';
import ToggleRow from '../fields/ToggleRow';
import {
  Block,
  Divider,
  Hint,
  PanelHead,
  ReadOnlyScope,
  SectionLabel,
  TextAreaField,
  TextField,
} from './parts';
import RecipientsPicker from './RecipientsPicker';

// Form-wide settings (plan §6 A2 "Form Settings", design: Properties with
// no field selected): name, description, account, web layout, behaviour
// toggles, notification recipients, then Duplicate / Archive.
//
// On a `w9` form (BuilderContext `kind`, w9-plan §6.5) only the
// Notifications block renders: the name, account, layout, behaviour and
// the Duplicate / Archive actions are fixed by the server.

const LAYOUTS = [
  { label: 'Standard scroll', value: 'scroll' },
  { label: 'Wizard', value: 'wizard' },
];

const LAYOUT_GUIDANCE = {
  scroll:
    'The whole form on one scrolling page with a section rail. Best for short forms, or when people want to scan everything before filling it in.',
  wizard:
    'One section per step with a review screen at the end. Best for long forms — people see one thing at a time and can’t skip required fields.',
};

// The form's own account stays pickable even when it's not in the active
// list (an inactive account).
export const accountOptions = (venues, accountId, accountName) => {
  const options = venues.map((v) => ({ label: v.name, value: v.id }));
  if (accountId && !options.some((o) => o.value === accountId)) {
    options.unshift({
      label: accountName || `Account ${accountId}`,
      value: accountId,
    });
  }
  return options;
};

const Behaviour = ({ working, set }) => (
  <>
    <SectionLabel>Behavior</SectionLabel>
    <ToggleRow
      first
      label="Progress bar"
      sub="Show completion in the header"
      on={working.show_progress}
      onChange={(v) => set('show_progress', v)}
    />
    <ToggleRow
      label="Confirmation email"
      sub="Email a receipt on submit"
      on={working.confirmation_email}
      onChange={(v) => set('confirmation_email', v)}
    />
    <ToggleRow
      help="save-resume"
      label="Save & resume"
      sub="Saves their progress on that device so they can finish later"
      on={working.save_and_resume}
      onChange={(v) => set('save_and_resume', v)}
    />
  </>
);

Behaviour.propTypes = {
  working: PropTypes.object.isRequired,
  set: PropTypes.func.isRequired,
};

const Notifications = ({ working, set, error }) => (
  <>
    <SectionLabel>Notifications</SectionLabel>
    <ToggleRow
      first
      label="Email on new submission"
      sub="Send the full entry to your team"
      on={working.notify_on_submit}
      onChange={(v) => set('notify_on_submit', v)}
    />
    {working.notify_on_submit ? (
      <div style={{ marginTop: 8 }}>
        <RecipientsPicker
          accountId={working.account_id}
          value={working.notify_recipients}
          onChange={(list) => set('notify_recipients', list)}
          error={error}
        />
      </div>
    ) : null}
  </>
);

Notifications.propTypes = {
  working: PropTypes.object.isRequired,
  set: PropTypes.func.isRequired,
  error: PropTypes.string,
};

Notifications.defaultProps = { error: null };

const FormSettings = ({ showHeader }) => {
  const {
    state,
    dispatch,
    readOnly,
    venues,
    accountName,
    onDuplicateForm,
    onArchiveForm,
    kind,
  } = useBuilder();
  const { working, errors } = state;
  const set = (key, value) => dispatch({ type: 'SET_SETTING', key, value });
  if (kind === 'w9') {
    return (
      <div data-tour="form-settings" data-fb-w9-settings="">
        {showHeader ? (
          <PanelHead
            icon="sliders-horizontal"
            title="W-9 Settings"
            sub="Who hears about new W-9s"
          />
        ) : null}
        <div style={{ padding: showHeader ? 18 : 0 }}>
          <ReadOnlyScope readOnly={readOnly}>
            <Notifications
              working={working}
              set={set}
              error={errors.notify_recipients}
            />
          </ReadOnlyScope>
        </div>
      </div>
    );
  }
  return (
    <div data-tour="form-settings">
      {showHeader ? (
        <PanelHead
          icon="sliders-horizontal"
          title="Form Settings"
          sub="Applies to the whole form"
        />
      ) : null}
      <div style={{ padding: showHeader ? 18 : 0 }}>
        <ReadOnlyScope readOnly={readOnly}>
          <TextField
            label="Form name"
            value={working.name}
            maxLength={LIMITS.formName}
            error={errors.name}
            onChange={(v) => set('name', v)}
          />
          <TextAreaField
            label="Description"
            value={working.description}
            placeholder="Shown at the top of the form"
            maxLength={LIMITS.formDescription}
            error={errors.description}
            onChange={(v) => set('description', v)}
          />
          <Block tight>
            <FormsSelect
              id="fb-settings-account"
              label="Account"
              value={working.account_id}
              options={accountOptions(venues, working.account_id, accountName)}
              placeholder="Choose an account"
              error={errors.account_id || null}
              disabled={readOnly}
              onChange={(id) => set('account_id', Number(id))}
            />
          </Block>
          <Hint top={0}>
            {`The track or series this form belongs to. It appears under that account in the ${
              brand().name
            } app.`}
          </Hint>
          <Divider />
          <SectionLabel>Web layout</SectionLabel>
          <Block>
            <Radio
              name="fb-web-layout"
              value={working.layout}
              options={LAYOUTS}
              helperText={LAYOUT_GUIDANCE[working.layout]}
              onChange={(e) => set('layout', e.target.value)}
            />
          </Block>
          <Divider />
          <Behaviour working={working} set={set} />
          <Divider />
          <Notifications
            working={working}
            set={set}
            error={errors.notify_recipients}
          />
        </ReadOnlyScope>
        <Divider />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <ButtonV2
            style={{ flex: 1, justifyContent: 'center' }}
            onClick={onDuplicateForm}
            icon={<Icon name="copy" size={15} color="currentColor" />}
          >
            Duplicate form
          </ButtonV2>
          {readOnly ? null : (
            <ButtonV2
              danger
              style={{ flex: 1, justifyContent: 'center' }}
              onClick={onArchiveForm}
              icon={<Icon name="archive" size={15} color="currentColor" />}
            >
              Archive form
            </ButtonV2>
          )}
        </div>
      </div>
    </div>
  );
};

FormSettings.propTypes = { showHeader: PropTypes.bool };
FormSettings.defaultProps = { showHeader: true };

export default FormSettings;
